import store from "@/store";
import { IContract } from "@/types";
import { Auth, CognitoUser } from "@aws-amplify/auth";
import { CurrentUserOpts, SignOutOpts } from "@aws-amplify/auth/lib-esm/types";
import { Sha256 } from "@aws-crypto/sha256-browser";
import { SignatureV4 } from "@smithy/signature-v4";
import { HttpRequest } from "@smithy/types";
import AWS from "aws-sdk";
import axios from "axios";
import moment from "moment-timezone";
import StarBridgeAuthService from "./starbridge.auth.service";

export enum Persistence {
  LOCAL = "local",
  SESSION = "session",
}

export interface ICredentialsResponse {
  AccessKeyId: string;
  SecretAccessKey: string;
  SessionToken: string;
  Expiration: string;
  tenantId: string;
  contractId: string;
  serviceGroupId: string;
  lcToken: string;
}

export interface ServiceCredentialsMap {
  "execute-api"?: ICredentials | null;
  "cognito-idp"?: ICredentials | null;
  dynamodb?: ICredentials | null;
  s3?: ICredentials | null;
}

export interface ICredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expireTime: string;
  contractId: string;
  serviceGroupId: string;
  tenantId: string;
  _version: string;
}

export interface UserInfo {
  id: string;
  username: string;
  attributes: {
    sub: string;
    given_name: string;
    family_name: string;
    email: string;
    "custom:tenant_id": string;
    "custom:last_login": string;
    email_verified: "True" | string;
    website: string;
    "custom:system_role"?: string;
  };
}

export interface AuthConfig {
  tenantId: string;
  tenantPreferredId: string;
  userPoolId: string;
  userPoolWebClientId: string;
  identityPoolId: string;
  _version?: string;
}

export interface AuthCognitoUser extends UserInfo, CognitoUser {}

export default class AuthService {
  public static readonly api = axios.create();
  public static readonly API_BASE_URL = process.env.VUE_APP_API_BASE_URL;
  private static _currentCredentialsPromise = {
    "execute-api": null as Promise<ICredentials | undefined | null> | null,
    s3: null as Promise<ICredentials | undefined | null> | null,
    "cognito-idp": null as Promise<ICredentials | undefined | null> | null,
    dynamodb: null as Promise<ICredentials | undefined | null> | null,
  };

  /**
   * This is ISSO authentication for user
   *
   * @static
   * @async
   * @param {string} token
   */
  static async _currentCredentials(
    service: keyof ServiceCredentialsMap = "execute-api",
    opts = { bypassCache: false },
  ): Promise<ICredentials | null> {
    // if sys admin return current credentials by @aws-amplify/auth
    if (store.state.isAdminApp) {
      return (await Auth.currentCredentials()) as unknown as ICredentials;
    }

    let cachedServiceCredentialsMap: ServiceCredentialsMap = AuthService.getCachedAuthCredentialsConfig(
      AuthService.currentTenantId(),
    );

    let serviceCachedCredentials = cachedServiceCredentialsMap[service];
    try {
      if (
        !opts.bypassCache &&
        serviceCachedCredentials &&
        moment(serviceCachedCredentials.expireTime).diff(moment(), "s") >= 0 &&
        serviceCachedCredentials._version === process.env.VUE_APP_VERSION &&
        serviceCachedCredentials.contractId === AuthService.currentContractId() &&
        serviceCachedCredentials.serviceGroupId === AuthService.currentServiceGroupId()
      ) {
        return serviceCachedCredentials;
      }
    } catch (error) {
      serviceCachedCredentials = undefined;
      console.error(error);
    }

    // Handle re-authenticate when expired token
    if (serviceCachedCredentials && moment(serviceCachedCredentials.expireTime).diff(moment(), "s") < 0) {
      console.log("Start refresh credentials without token", service);
      await StarBridgeAuthService.authenticate();
      if (service === "execute-api") {
        return AuthService.getCachedAuthCredentialsConfig(AuthService.currentTenantId())[service] || null;
      }
    }

    const url =
      this.API_BASE_URL +
      `/contract/${AuthService.currentContractId()}/group/${AuthService.currentServiceGroupId()}/auth`;
    await AuthService.api
      .post(url, { service: service })
      .then((response) => {
        if (response.status == 200) {
          AuthService.setServiceCachedAuthCredentialsConfig(AuthService.currentTenantId(), service, "");
          const credentials = response.data as ICredentialsResponse;
          const newCredentials = {
            accessKeyId: credentials.AccessKeyId || "",
            secretAccessKey: credentials.SecretAccessKey || "",
            sessionToken: credentials.SessionToken,
            expireTime: credentials.Expiration,
            contractId: credentials.contractId,
            serviceGroupId: credentials.serviceGroupId,
            _version: process.env.VUE_APP_VERSION as string,
          };
          AuthService.setServiceCachedAuthCredentialsConfig(AuthService.currentTenantId(), service, newCredentials);
        }
      })
      .catch((err) => {
        console.log("err", err);
      });
    cachedServiceCredentialsMap = AuthService.getCachedAuthCredentialsConfig(AuthService.currentTenantId());

    if (service == "execute-api") {
      AWS.config.update({
        credentials: cachedServiceCredentialsMap[service],
      });
    }

    return cachedServiceCredentialsMap[service] || null;
  }

  static async currentUserInfo(params?: CurrentUserOpts | undefined): Promise<UserInfo> {
    const user = await AuthService.currentAuthenticatedUser(params);
    return {
      id: user.id,
      username: user.username,
      attributes: user.attributes,
    };
  }

  static async currentAuthenticatedUser(params?: CurrentUserOpts | undefined): Promise<AuthCognitoUser> {
    let user: AuthCognitoUser = await Auth.currentAuthenticatedUser(params);
    if (user) {
      const idTokenExpiration = user.getSignInUserSession()?.getIdToken().getExpiration();
      if (idTokenExpiration && idTokenExpiration * 1000 < new Date().getTime() + 3 * 60000) {
        user = await Auth.currentAuthenticatedUser({ ...params, bypassCache: true });
      }
    }
    return user;
  }

  static currentContractId() {
    const contract: IContract | null = store.getters.contract;
    if (contract) return contract.contractId;
    return "";
  }

  static currentServiceGroupId() {
    const contract: IContract | null = store.getters.contract;
    if (contract) return contract.serviceGroupId;
    return "";
  }

  static currentTenantId() {
    const contract: IContract | null = store.getters.contract;
    if (contract) return `TENANT${contract.contractId}@${contract.serviceGroupId}`;
    return "";
  }

  static async currentCredentials(service: keyof ServiceCredentialsMap = "execute-api", opts = { bypassCache: false }) {
    if (!service) service = "execute-api";
    if (!AuthService._currentCredentialsPromise[service]) {
      AuthService._currentCredentialsPromise[service] = AuthService._currentCredentials(service, opts);
    }
    return (AuthService._currentCredentialsPromise[service] as Promise<ICredentials>)
      .then((r) => {
        AuthService._currentCredentialsPromise[service] = null;
        return r;
      })
      .catch((r: any) => {
        AuthService._currentCredentialsPromise[service] = null;
        throw r;
      });
  }

  static getPersistence(tenantId: string): Persistence {
    return (localStorage.getItem(`AuthService.${tenantId}.persistence`) as Persistence) || Persistence.SESSION;
  }

  static setPersistence(tenantPreferredId: string, persistence: Persistence) {
    localStorage.setItem(`AuthService.${tenantPreferredId}.persistence`, persistence);
  }

  static getCachedAuthCredentialsConfig(tenantId: string) {
    const store = AuthService.getPersistence(tenantId) === Persistence.SESSION ? sessionStorage : localStorage;
    let credentials: ServiceCredentialsMap = {
      "execute-api": undefined,
      dynamodb: undefined,
      "cognito-idp": undefined,
      s3: undefined,
    };

    credentials = {
      ...credentials,
      ...JSON.parse(store.getItem(`AuthService.${tenantId}.credentialsConfig`) || "{}"),
    };
    return credentials;
  }

  static setCachedAuthCredentialsConfig(tenantId: string, credentialsConfig: any) {
    const store = AuthService.getPersistence(tenantId) === Persistence.SESSION ? sessionStorage : localStorage;
    if (credentialsConfig === null) store.removeItem(`AuthService.${tenantId}.credentialsConfig`);
    else store.setItem(`AuthService.${tenantId}.credentialsConfig`, JSON.stringify(credentialsConfig));
  }

  static setServiceCachedAuthCredentialsConfig(
    tenantId: string,
    service: keyof ServiceCredentialsMap,
    credentials: any,
  ) {
    if (tenantId) {
      const credentialsConfig = AuthService.getCachedAuthCredentialsConfig(tenantId);
      credentialsConfig[service] = credentials;
      AuthService.setCachedAuthCredentialsConfig(tenantId, credentialsConfig);
    }
  }

  static clearCachedAuthCredentialsConfig(tenantId: string) {
    return AuthService.setCachedAuthCredentialsConfig(tenantId, {
      "execute-api": null,
      dynamodb: null,
      "cognito-idp": null,
      s3: null,
    });
  }

  static async configure() {
    const persistence = AuthService.getPersistence(process.env.VUE_APP_SYSADMIN_TENANT_PREFERRED_ID || "");
    Auth.configure({
      region: process.env.VUE_APP_AWS_REGION,
      authenticationFlowType: "USER_SRP_AUTH",
      userPoolId: process.env.VUE_APP_SYSADMIN_USER_POOL_ID,
      userPoolWebClientId: process.env.VUE_APP_SYSADMIN_USER_POOL_WEB_CLIENT_ID,
      identityPoolId: process.env.VUE_APP_SYSADMIN_IDENTITY_POOL_ID,
      storage: persistence === Persistence.LOCAL ? localStorage : sessionStorage,
    });
  }

  static async signOut() {
    this.clearCachedAuthCredentialsConfig(AuthService.currentTenantId());
    store.dispatch("signOut");
  }

  static async sysAdminSignIn({
    username,
    password,
  }: {
    username: string;
    password: string;
  }): Promise<AuthCognitoUser> {
    await AuthService.configure();

    return await Auth.signIn({
      username,
      password,
    });
  }

  static async sysAdminSignOut(opts?: SignOutOpts) {
    try {
      return await Auth.signOut(opts);
    } catch (error) {
      console.warn(error);
    }
  }
}

AuthService.api.interceptors.request.use(async (config) => {
  const credentials = await AuthService.currentCredentials();
  if (!credentials) return config;

  const url = config.url ?? "";
  const parsedUrl = new URL(url);

  const signer = new SignatureV4({
    service: "execute-api",
    region: process.env.VUE_APP_AWS_REGION as string,
    credentials: credentials,
    sha256: Sha256,
  });

  // Build the request object
  const requestToSign = {
    hostname: parsedUrl.hostname.toString(),
    path: parsedUrl.pathname,
    method: config.method?.toUpperCase() ?? "GET",
    protocol: parsedUrl.protocol,
    body: typeof config.data === "string" ? config.data : JSON.stringify(config.data),
    query: parsedUrl.searchParams ? Object.fromEntries(parsedUrl.searchParams) : undefined,
    headers: {
      host: parsedUrl.host.toString(),
    },
  } as HttpRequest;

  if (config.data) {
    requestToSign.headers["Content-Type"] = "application/json";
  }

  const signedRequest = await signer.sign(requestToSign as HttpRequest);
  delete signedRequest.headers["host"];

  // Convert signed headers into AxiosHeaders instance
  const axiosHeaders = new axios.AxiosHeaders({
    ...config.headers,
    ...Object.fromEntries(Object.entries(signedRequest.headers).map(([key, value]) => [key.toLowerCase(), value])),
  });

  // Update Axios config headers
  config.headers = axiosHeaders;

  return config;
});
